import { FC, ReactNode, useEffect, useRef, useState } from "react";
import {
  Badge,
  Button,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from "@capacit-com/lara-law-shared-ui";
import {
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  Sparkles,
  X,
  type LucideIcon,
} from "lucide-react";
import { useDashboards, useWidgetTemplates } from "@/hooks/useDashboard";
import type { Dashboard, WidgetInstance } from "@/models/dashboard/Dashboard";
import type { WidgetConfig, WidgetType } from "@/models/dashboard/WidgetConfig";
import useDashboardStore from "@/stores/dashboard.store";
import {
  CONTENT_WIDGET_TYPES,
  USER_DATA_WIDGET_TYPES,
  WIDGET_TYPE_INFO,
  getDefaultWidgetConfig,
  getWidgetTypeInfo,
} from "../lib/widgetCatalog";

export interface GalleryPick {
  type: WidgetType;
  name: string;
  config: WidgetConfig;
  size?: { w: number; h: number };
}

interface WidgetGalleryProps {
  onPick: (pick: GalleryPick) => void;
  onClose: () => void;
}

type GalleryView =
  | { kind: "types" }
  | { kind: "templates" }
  | { kind: "dashboards" }
  | { kind: "dashboard-widgets"; dashboard: Dashboard };

const GalleryEntry: FC<{
  icon: LucideIcon;
  label: string;
  description?: ReactNode;
  tag?: string;
  showChevron?: boolean;
  onClick: () => void;
}> = ({ icon: Icon, label, description, tag, showChevron, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors hover:border-primary/60 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
  >
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
      <Icon className="h-4 w-4" aria-hidden="true" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{label}</span>
        {tag && (
          <Badge variant="secondary" className="text-xs font-normal">
            {tag}
          </Badge>
        )}
      </span>
      {description && (
        <span className="mt-0.5 block text-xs text-muted-foreground">
          {description}
        </span>
      )}
    </span>
    {showChevron && (
      <ChevronRight
        className="mt-2 h-4 w-4 shrink-0 text-muted-foreground"
        aria-hidden="true"
      />
    )}
  </button>
);

const GallerySection: FC<{ title: string; children: ReactNode }> = ({
  title,
  children,
}) => (
  <section className="space-y-2">
    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {title}
    </h3>
    {children}
  </section>
);

/** Names a few entries of a list, e.g. "Dagpenge-pakken, Efterløn, Nyt fra STAR". */
const listNames = (names: string[]) =>
  names.length > 3 ? `${names.slice(0, 3).join(", ")} …` : names.join(", ");

/** The "Tilføj widget" view of the editor panel (LAR-726): every widget type and every way to reuse one. */
const WidgetGallery: FC<WidgetGalleryProps> = ({ onPick, onClose }) => {
  const [view, setView] = useState<GalleryView>({ kind: "types" });
  const entriesRef = useRef<HTMLDivElement>(null);

  // Focus moves into the panel when it opens, and to the first entry of each view.
  useEffect(() => {
    entriesRef.current
      ?.querySelector<HTMLButtonElement>("button:not([disabled])")
      ?.focus();
  }, [view]);
  const { data: templates, isLoading: isLoadingTemplates } =
    useWidgetTemplates();
  const { data: dashboards } = useDashboards();
  const selectedDashboardId = useDashboardStore((s) => s.selectedDashboardId);
  const widgetsOnDashboard = useDashboardStore((s) => s.localConfig?.widgets);
  const typesOnDashboard = new Set(widgetsOnDashboard?.map((w) => w.type));

  const otherDashboards =
    dashboards?.filter(
      (d) => d.id !== selectedDashboardId && d.config.widgets.length > 0
    ) ?? [];

  const handlePickType = (type: WidgetType) =>
    onPick({
      type,
      name: WIDGET_TYPE_INFO[type].label,
      config: getDefaultWidgetConfig(type),
    });

  const handlePickCopy = (widget: WidgetInstance) =>
    onPick({
      type: widget.type,
      name: widget.name,
      config: structuredClone(widget.config),
      size: { w: widget.position.w, h: widget.position.h },
    });

  const backButton = (label: string, to: GalleryView) => (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-2 self-start"
      onClick={() => setView(to)}
    >
      <ChevronLeft className="mr-1 h-4 w-4" />
      {label}
    </Button>
  );

  const renderView = () => {
    switch (view.kind) {
      case "types":
        return (
          <>
            <GallerySection title="Indhold">
              {CONTENT_WIDGET_TYPES.map((type) => (
                <GalleryEntry
                  key={type}
                  icon={WIDGET_TYPE_INFO[type].icon}
                  label={WIDGET_TYPE_INFO[type].label}
                  description={WIDGET_TYPE_INFO[type].description}
                  onClick={() => handlePickType(type)}
                />
              ))}
            </GallerySection>
            <GallerySection title="Dine data">
              {USER_DATA_WIDGET_TYPES.map((type) => (
                <GalleryEntry
                  key={type}
                  icon={WIDGET_TYPE_INFO[type].icon}
                  label={WIDGET_TYPE_INFO[type].label}
                  description={WIDGET_TYPE_INFO[type].description}
                  tag={
                    typesOnDashboard.has(type)
                      ? "Allerede på dashboardet"
                      : undefined
                  }
                  onClick={() => handlePickType(type)}
                />
              ))}
            </GallerySection>
            <GallerySection title="Genbrug">
              <GalleryEntry
                icon={Sparkles}
                label="Fra organisationens skabeloner"
                description={
                  isLoadingTemplates
                    ? "Henter skabeloner …"
                    : templates && templates.length > 0
                      ? `${templates.length} ${templates.length === 1 ? "skabelon" : "skabeloner"} · ${listNames(templates.map((t) => t.name))}`
                      : "Ingen skabeloner endnu"
                }
                showChevron
                onClick={() => setView({ kind: "templates" })}
              />
              <GalleryEntry
                icon={LayoutDashboard}
                label="Fra et andet dashboard"
                description={
                  otherDashboards.length > 0
                    ? `Kopiér en widget fra ${listNames(otherDashboards.map((d) => d.name))}`
                    : "Ingen andre dashboards med widgets"
                }
                showChevron
                onClick={() => setView({ kind: "dashboards" })}
              />
            </GallerySection>
          </>
        );
      case "templates":
        return (
          <GallerySection title="Organisationens skabeloner">
            {backButton("Tilbage", { kind: "types" })}
            {isLoadingTemplates ? (
              <div className="space-y-2">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : !templates || templates.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Din organisation har endnu ikke oprettet widget-skabeloner.
              </p>
            ) : (
              templates.map((template) => (
                <GalleryEntry
                  key={template.id}
                  icon={getWidgetTypeInfo(template.config.type).icon}
                  label={template.name}
                  description={
                    template.description ||
                    getWidgetTypeInfo(template.config.type).label
                  }
                  onClick={() =>
                    onPick({
                      type: template.config.type,
                      name: template.config.defaultName,
                      config: structuredClone(template.config.config),
                      size: template.config.defaultSize,
                    })
                  }
                />
              ))
            )}
          </GallerySection>
        );
      case "dashboards":
        return (
          <GallerySection title="Fra et andet dashboard">
            {backButton("Tilbage", { kind: "types" })}
            {otherDashboards.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Du har ikke andre dashboards med widgets at kopiere fra.
              </p>
            ) : (
              otherDashboards.map((dashboard) => (
                <GalleryEntry
                  key={dashboard.id}
                  icon={LayoutDashboard}
                  label={dashboard.name}
                  description={`${dashboard.config.widgets.length} ${dashboard.config.widgets.length === 1 ? "widget" : "widgets"}`}
                  showChevron
                  onClick={() =>
                    setView({ kind: "dashboard-widgets", dashboard })
                  }
                />
              ))
            )}
          </GallerySection>
        );
      case "dashboard-widgets":
        return (
          <GallerySection title={view.dashboard.name}>
            {backButton("Andre dashboards", { kind: "dashboards" })}
            {view.dashboard.config.widgets.map((widget) => (
              <GalleryEntry
                key={widget.id}
                icon={getWidgetTypeInfo(widget.type).icon}
                label={widget.name}
                description={getWidgetTypeInfo(widget.type).label}
                onClick={() => handlePickCopy(widget)}
              />
            ))}
          </GallerySection>
        );
    }
  };

  return (
    <>
      <SheetHeader className="flex-row items-start justify-between gap-3 border-b p-4">
        <div className="space-y-1">
          <SheetTitle>Tilføj widget</SheetTitle>
          <SheetDescription>
            Vælg en type. Widgetten placeres på dashboardet, og du kan redigere
            den med det samme.
          </SheetDescription>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onClose}
          aria-label="Luk panelet"
        >
          <X className="h-4 w-4" />
        </Button>
      </SheetHeader>
      <div
        ref={entriesRef}
        className="flex flex-col gap-5 p-4 md:min-h-0 md:flex-1 md:overflow-y-auto"
      >
        {renderView()}
      </div>
    </>
  );
};

export default WidgetGallery;
